import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import type { JobOpeningStatus, StageBar, StageBarKey } from '@nest-recruit/shared';
import { MenuItem, Popover } from '../../../components/Popover';
import { formatNumber } from '../../../lib/format';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { stageTrackOf, trackListOf, trackSummaryOf, type TrackDot } from './stage-track';
import styles from './StageTrack.module.css';

// JOB-01's Stages column (owner's pick B, DECISIONS-LOG 2026-10-27): a slim progress track and
// the stages that hold people in words. Pointing at the cell, or clicking it, opens every stage's
// count beside it (CLAUDE.md §5: anchored to its trigger); a stage there opens the job at it.

/** Long enough that running the pointer down the column doesn't flash a pop-up on every row. */
const OPEN_DELAY_MS = 350;
/** Long enough to cross the gap from the cell into the pop-up without it closing. */
const CLOSE_DELAY_MS = 200;
const LIST_WIDTH = 260;

interface StageTrackCellProps {
  readonly jobId: string;
  readonly title: string;
  readonly status: JobOpeningStatus;
  readonly bar: StageBar;
  readonly hiddenStages: ReadonlySet<StageBarKey>;
}

export function StageTrackCell({ jobId, title, status, bar, hiddenStages }: StageTrackCellProps): ReactNode {
  const track = stageTrackOf(bar, status, hiddenStages);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  // Opened by a click stays open until dismissed; opened by pointing closes when the pointer leaves.
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const later = (work: () => void, delay: number): void => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(work, delay);
  };
  const show = (): void => {
    if (triggerRef.current) setAnchor(anchorFrom(triggerRef.current));
  };
  const hide = (): void => {
    setAnchor(null);
    setIsPinned(false);
  };
  const onPointerEnter = (): void => later(show, anchor ? 0 : OPEN_DELAY_MS);
  const onPointerLeave = (): void => {
    if (isPinned) return window.clearTimeout(timer.current);
    later(hide, CLOSE_DELAY_MS);
  };
  const toggle = (event: MouseEvent): void => {
    // The cell is inside a row that opens the job on click; this click is the cell's own.
    event.stopPropagation();
    window.clearTimeout(timer.current);
    if (anchor && isPinned) return hide();
    setIsPinned(true);
    show();
  };
  // Enter and Space belong to this button, not to the table's "open the focused row".
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
  };
  const close = (): void => {
    hide();
    triggerRef.current?.focus();
  };

  const summary = trackSummaryOf(track);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.cell}
        aria-label={`Stages of ${title}: ${summary}. Show every stage.`}
        aria-expanded={anchor !== null}
        onClick={toggle}
        onKeyDown={onKeyDown}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
      >
        <span className={styles.track} data-ended={track.isEnded} aria-hidden>
          {track.slots.map((slot, index) => (
            // The line between two dots is coloured up to the "now" dot: how far the job has come.
            <span key={slot.key} className={styles.slot} data-line-in={index > 0 && index <= track.fillTo} data-line-out={index < track.fillTo}>
              <span className={styles.dot} data-dot={slot.dot} data-rounds={slot.rounds.length > 1 ? slot.rounds.length : undefined}>
                {slot.rounds.length > 1 && slot.rounds.map((round) => <i key={round.key} className={styles.round} data-dot={round.dot} />)}
              </span>
            </span>
          ))}
        </span>
        <Words track={track} />
      </button>
      <Popover anchor={anchor} onClose={close} width={LIST_WIDTH} label={`Stages of ${title}`}>
        <div className={styles.list} onPointerEnter={() => window.clearTimeout(timer.current)} onPointerLeave={onPointerLeave}>
          <p className={styles.listTitle}>{title} · stages</p>
          {trackListOf(bar, hiddenStages).map((row) => (
            <MenuItem
              key={row.key}
              href={`/jobs/${jobId}/${row.key}`}
              isSelected={row.dot === 'now'}
              icon={<ListDot dot={row.dot} isRound={row.isRound} isEnded={track.isEnded} />}
              meta={<span className={styles.listCount} data-zero={row.count === 0}>{formatNumber(row.count)}</span>}
            >
              {row.label}
            </MenuItem>
          ))}
          <p className={styles.listHint}>Click a stage to open the job there</p>
        </div>
      </Popover>
    </>
  );
}

function Words({ track }: { readonly track: ReturnType<typeof stageTrackOf> }): ReactNode {
  if (track.emptyText !== null) return <span className={`${styles.words} ${styles.quiet}`}>{track.emptyText}</span>;
  return (
    <span className={styles.words} aria-hidden>
      {track.words.map((word, index) => (
        <span key={word.label} className={index === 0 ? styles.lead : undefined}>
          {index > 0 && <span className={styles.sep}>·</span>}
          {word.label} <span className={styles.num}>{formatNumber(word.count)}</span>
        </span>
      ))}
      {track.more > 0 && <span className={styles.more}>+{track.more}</span>}
    </span>
  );
}

function ListDot({ dot, isRound, isEnded }: { readonly dot: TrackDot; readonly isRound: boolean; readonly isEnded: boolean }): ReactNode {
  return (
    <span className={styles.listDotWrap} data-round={isRound} data-now={dot === 'now'}>
      <span className={`${styles.dot} ${styles.listDot}`} data-dot={dot} data-ended={isEnded} />
    </span>
  );
}
